<template>
  <base-slide-over ref="slideover" :title="title" size="lg">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingPrograms || isLoadingMappings">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-full">
          <base-skeleton-loader class="tw-h-3 tw-w-full" />
        </div>
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-grid tw-grid-cols-1 tw-gap-y-6">
        <div class="tw-flex tw-items-start">
          <base-advanced-select
            label="Locations"
            placeholder="Select one or more locations"
            :options="orderedLocations"
            v-model="selectedLocations"
            label-key="name"
            multiple
            :required="true"
            class="tw-flex-grow"
            @input="handleLocationSelectionChange"
          />
        </div>
        <div class="tw-flex tw-items-start">
          <base-select
            label="Program"
            placeholder="Select a program"
            :options="programsForSelect"
            label-key="label"
            value-key="value"
            :value="selectedProgramName"
            @input="handleProgramSelection"
            :required="true"
            class="tw-flex-grow"
            :disabled="selectedLocations.length === 0"
          />
        </div>

        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-x-4 tw-gap-y-4 tw-mt-2" v-if="selectedProgramName && dynamicPriceInputs.length > 0">
          <div v-for="programInput in dynamicPriceInputs" :key="programInput.businessProgramId" class="tw-col-span-1">
            <base-currency-input
              :label="formatProgramGroupName(programInput.programGroup)"
              :value="programInput.inputPriceDisplay"
              @input="programInput.inputPriceDisplay = $event"
              :int-value.sync="programInput.inputPriceCents"
              :precision="0"
              :min="programInput.priceFloor"
              :max="programInput.ceilingPrice"
            />
          </div>
        </div>
        <div v-else-if="selectedProgramName && dynamicPriceInputs.length === 0" class="tw-text-gray-500 tw-italic">
          No specific price groups found for this program.
        </div>

        <div class="tw-text-sm tw-mt-2" v-if="selectedProgramName">
          <div class="tw-flex tw-flex-wrap tw-gap-4 tw-mb-4 tw-font-medium">
            <p v-if="formatCentsToDisplay(yourPriceCents) > 0">
              Your Price: <span class="tw-font-normal">${{ formatCentsToDisplay(yourPriceCents) }}</span>
            </p>
            <p v-if="dynamicPriceInputs[0].priceFloor > 0">
              Floor Price: <span class="tw-font-normal">${{ dynamicPriceInputs[0].priceFloor }}</span>
            </p>
            <p v-if="dynamicPriceInputs[0].ceilingPrice > 0">
              Ceiling Price: <span class="tw-font-normal">${{ dynamicPriceInputs[0].ceilingPrice }}</span>
            </p>
          </div>
          <div v-if="dynamicPriceInputs.length > 0">
            <div class="tw-mb-4" v-if="hasAnyNonZeroSuggestedPrice">
              <p class="tw-mb-2 tw-font-sm tw-text-primary">Our Suggested Patients Price Tiers</p>

              <div v-for="programInput in dynamicPriceInputs" :key="`summary-${programInput.businessProgramId}`" class="tw-flex tw-items-center tw-mb-1 tw-w-full">
                <div class="tw-font-medium tw-truncate" v-if="formatCentsToDisplay(programInput.originalSuggestedPriceCents) > 0">
                  {{ formatProgramGroupName(programInput.programGroup) }}: ${{ formatCentsToDisplay(programInput.originalSuggestedPriceCents) }}
                </div>
              </div>
            </div>
            <div>
              <div v-if="hasAnyMappedPlans">
                <p class="tw-mb-2 tw-font-sm tw-text-primary">Your Existing Plans Tier</p>

                <div v-for="programInput in dynamicPriceInputs" :key="`summary-existing-${programInput.businessProgramId}`" class="tw-flex tw-items-center tw-mb-1 tw-w-full">
                  <div class="tw-font-medium tw-truncate" v-if="programInput.businessPlanName !== 'No Plan Map'">
                    {{ formatProgramGroupName(programInput.programGroup) }}: {{ programInput.businessPlanName }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p class="tw-text-sm tw-text-gray-500 tw-mt-0">
          Please note price changes will only take effect after the end of patient's current plan cycle.
        </p>
      </div>
    </div>

    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()">
          Cancel
        </base-button>
        <base-button :loading="isSaving" class="tw-w-auto" @click="save" :disabled="hasPriceError">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { orderBy } from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "BusinessLocationCostMappingsSlideover",

  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingPrograms: false,
      isLoadingMappings: false,
      isSaving: false,

      selectedLocations: [],
      programsList: [],
      programPricesMap: {},
      selectedProgramName: null,
      dynamicPriceInputs: [], // Array of program objects augmented with input values
      yourPriceCents: 0, // For displaying "Your Price" in the summary (derived from provider price)
      hasPriceError: false,
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]), // Map the 'locations' getter which provides all accessible locations

    orderedLocations() {
      return this.locations.slice().sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    },
    title() {
      return "Request Price Change";
    },

    programsForSelect() {
      const uniqueProgramNames = Array.from(new Set(this.programsList.map((p) => p.name)));
      return orderBy(uniqueProgramNames, [], ["asc"]).map((name) => ({
        label: name,
        value: name,
      }));
    },
    hasAnyNonZeroSuggestedPrice() {
      // Only proceed if there are dynamic price inputs
      if (this.dynamicPriceInputs.length === 0) {
        return false;
      }
      // Check if ANY item, after being processed by formatCentsToDisplay, results in a value > 0
      return this.dynamicPriceInputs.some((input) => this.formatCentsToDisplay(input.originalSuggestedPriceCents) > 0);
    },
    hasAnyMappedPlans() {
      if (this.dynamicPriceInputs.length === 0) {
        return false;
      }
      return this.dynamicPriceInputs.some((input) => input.businessPlanName !== "No Plan Map");
    },
  },
  methods: {
    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents) || cents === null || cents === undefined) {
        return "0.00";
      }
      return cents / 100;
    },
    async show() {
      this.resetData();

      this.$refs.slideover.show();
      await this.fetchAndPrepareProgramData();

      // If locations are pre-selected, immediately try to fetch mappings for the first one
      if (this.selectedLocations.length > 0) {
        await this.getPricesMappings(this.selectedLocations[0].businessLocationId);
      }
    },
    hide() {
      this.$refs.slideover.hide();
      this.resetData();
    },
    resetData() {
      this.isLoadingPrograms = false;
      this.isLoadingMappings = false;
      this.isSaving = false;
      this.selectedLocations = [];
      this.programsList = [];
      this.programPricesMap = {};
      this.selectedProgramName = null;
      this.dynamicPriceInputs = [];
      this.yourPriceCents = 0;
    },
    async fetchAndPrepareProgramData() {
      try {
        await this.getPrograms();
        // Mappings will now be fetched when selectedLocations changes or on show
        // if locations are pre-selected.
      } catch (error) {
        console.error("Error fetching or preparing program data:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program data",
          message: "There was an issue fetching program options. Please try again.",
          error,
        });
      }
    },
    async getPrograms() {
      try {
        this.isLoadingPrograms = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);
        this.programsList = response.data;
      } catch (error) {
        console.error("Error fetching master programs:", error);
        throw error;
      } finally {
        this.isLoadingPrograms = false;
      }
    },
    async getPricesMappings(locationId) {
      if (!locationId) {
        this.programPricesMap = {}; // Clear map if no locationId
        this.dynamicPriceInputs = [];
        this.yourPriceCents = 0;
        console.warn("getPricesMappings called without a locationId. Clearing mappings.");
        return;
      }
      try {
        this.isLoadingMappings = true;
        const url = `/businesses/${this.businessId}/locations/${locationId}/mappings/program-prices`;
        const response = await this.$axios.get(url);
        this.programPricesMap = {}; // Reset map before populating
        if (Array.isArray(response.data)) {
          response.data.forEach((mapping) => {
            if (mapping.businessProgramId) {
              this.programPricesMap[mapping.businessProgramId] = {
                suggestedPriceCents: mapping.suggestedPriceCents || 0,
                providerPriceCents: mapping.providerPriceCents || 0,
                ceilingPriceCents: mapping.ceilingPriceCents || 0,
                priceFloorCents: mapping.priceFloorCents || 0,
                businessPlanName: mapping.businessPlanName || "No Plan Map",
                businessPlanId: mapping.businessPlanId || null,
                businessPlanPriceCents: mapping.businessPlanPriceCents || 0,
              };
            }
          });
        }
        // Re-apply program selection to update dynamic inputs based on new mappings
        if (this.selectedProgramName) {
          this.handleProgramSelection(this.selectedProgramName);
        }
      } catch (error) {
        console.error("Error fetching program price mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program price mappings",
          message: "There was an issue fetching program pricing for the selected location. Please try again.",
          error,
        });
        throw error;
      } finally {
        this.isLoadingMappings = false;
      }
    },

    handleLocationSelectionChange() {
      // If a location is selected, fetch its mappings
      if (this.selectedLocations.length > 0) {
        this.getPricesMappings(this.selectedLocations[0].businessLocationId);
      } else {
        // If no locations are selected, clear current mappings and inputs
        this.programPricesMap = {};
        this.dynamicPriceInputs = [];
        this.yourPriceCents = 0;
        this.selectedProgramName = null;
      }
    },

    handleProgramSelection(programName) {
      this.selectedProgramName = programName;
      this.yourPriceCents = 0; // Reset "Your Price" on new selection

      const programsInSelectedGroup = this.programsList.filter((p) => p.name === programName);

      // Map these programs to create dynamic price inputs, pulling existing data from programPricesMap
      this.dynamicPriceInputs = programsInSelectedGroup.map((program) => {
        const existingMapping = this.programPricesMap[program.businessProgramId];
        const existingSuggestedPrice = existingMapping ? existingMapping.suggestedPriceCents || 0 : 0;
        const existingProviderPrice = existingMapping ? existingMapping.providerPriceCents || 0 : 0;
        const existingBusinessPlanName = existingMapping ? existingMapping.businessPlanName || "Plan Not Map" : "Plan Not Map";
        const existingBusinessPlanId = existingMapping ? existingMapping.businessPlanId || null : null;
        const existingBusinessPlanPrice = existingMapping ? existingMapping.businessPlanPriceCents || 0 : 0;

        const existingPriceFloor = existingMapping ? existingMapping.priceFloorCents || 0 : 0;
        const existingCeilingPrice = existingMapping ? existingMapping.ceilingPriceCents || 0 : 0;

        return {
          ...program,
          inputPriceCents: existingBusinessPlanPrice,
          inputPriceDisplay: this.formatCentsToDisplay(existingBusinessPlanPrice),
          originalSuggestedPriceCents: existingSuggestedPrice,
          providerPriceCents: existingProviderPrice,
          businessPlanName: existingBusinessPlanName,
          businessPlanId: existingBusinessPlanId,
          businessPlanPrice: existingBusinessPlanPrice,
          priceFloor: this.formatCentsToDisplay(existingPriceFloor),
          ceilingPrice: this.formatCentsToDisplay(existingCeilingPrice),
        };
      });

      this.dynamicPriceInputs = orderBy(this.dynamicPriceInputs, ["programGroup"], ["asc"]);

      if (this.dynamicPriceInputs.length > 0) {
        this.yourPriceCents = this.dynamicPriceInputs[0].providerPriceCents;
      } else {
        this.yourPriceCents = 0;
      }
      this.$nextTick(() => {
        this.dynamicPriceInputs.forEach((input) => {
          this.$watch(
            () => input.inputPriceDisplay,
            () => {
              this.checkPriceErrors();
            },
            { immediate: true },
          );
        });
      });
    },
    formatProgramGroupName(groupName) {
      if (!groupName) {
        return "";
      }

      groupName = groupName.replace("Pricing", "");

      const match = groupName.match(/^(\d+\s*-\s*|)(\s*)(.*)$/); // Matches "NUMBER - " or "NUMBER-" or similar
      if (match && match[3]) {
        return match[3]; // Returns the part after the hyphen and space
      }
      return groupName; // If no match, return original string
    },

    async save() {
      if (!this.selectedLocations || this.selectedLocations.length === 0) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Locations Not Selected",
          message: "Please select at least one location before submitting the request.",
          error: "Locations Not Selected",
        });
        return;
      }

      if (!this.selectedProgramName) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Program Not Selected",
          message: "Please select a program before submitting the request.",
        });
        return;
      }
      if (this.dynamicPriceInputs.length === 0) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "No Prices to Save",
          message: "No specific price groups found for the selected program to save.",
          error: "No Prices to Save",
        });
        return;
      }
      const priceSuggestionsToSubmit = this.dynamicPriceInputs
        .filter((programInput) => {
          // Only include if the input price is different from the original businessPlanPrice
          return programInput.inputPriceCents !== programInput.businessPlanPrice;
        })
        .map((programInput) => {
          return {
            businessProgramId: programInput.businessProgramId,
            changePrice: programInput.inputPriceCents * 100, //  rounded input price
            programGroup: programInput.programGroup,
            businessPlanId: programInput.businessPlanId,
            businessPlanName: programInput.businessPlanName,
            formattedProgramGroup: this.formatProgramGroupName(programInput.programGroup),
            originalPriceDisplay: this.formatCentsToDisplay(programInput.businessPlanPrice),
            currentProgramPrice: this.formatProgramGroupName(programInput.programGroup) + " ($" + this.formatCentsToDisplay(programInput.businessPlanPrice) + ")",
            newProgramPriceRequest: this.formatProgramGroupName(programInput.programGroup) + " ($" + programInput.inputPriceCents + ")",
            programName: this.selectedProgramName,
          };
        });

      // 3. Check if any price changes were detected
      if (priceSuggestionsToSubmit.length === 0) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "No Price Changes Detected",
          message: "The entered prices are the same as the existing plans for the selected program. Nothing will be submitted.",
          error: "No Price Changes Detected",
        });
        this.isSaving = false; // Ensure loading state is reset
        this.hide(); // Optionally hide the slideover if no changes are needed
        return;
      }

      this.isSaving = true;
      try {
        // Construct the payload for multiple locations
        const payload = {
          locationIds: this.selectedLocations.map((loc) => loc.businessLocationId), // Array of selected location IDs
          programName: this.selectedProgramName,
          priceSuggestion: JSON.stringify(priceSuggestionsToSubmit),
        };

        await this.$axios.post(`/businesses/${this.businessId}/request-program-price-change`, payload);

        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Price Change Requested",
          message: `Price change request submitted for ${this.selectedProgramName}.`,
        });
        this.hide();
        this.$emit("price-change-request-submitted");
      } catch (error) {
        console.error("Error submitting price change request:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to Request Price Change",
          message: `There was an issue submitting your price change. Please try again.`,
          error,
        });
      } finally {
        this.isSaving = false;
      }
    },
    checkPriceErrors() {
      this.hasPriceError = this.dynamicPriceInputs.some((input) => {
        const raw = String(input.inputPriceDisplay || "").replace(/[^0-9.-]+/g, "");
        const value = parseFloat(raw);
        const min = parseFloat(input.priceFloor);
        const max = parseFloat(input.ceilingPrice);

        const isError = isNaN(value) || (input.priceFloor != null && value < min) || (input.ceilingPrice != null && value > max) || !Number.isInteger(value);
        return isError;
      });
    },
  },
  watch: {
    dynamicPriceInputs: {
      handler: "checkPriceErrors",
      deep: true,
    },
  },
};
</script>
